import dayjs from 'dayjs';
import { isBeforeSchoolYear } from 'utils/isBeforeSchoolYear';
import { useAppQuery } from 'services/reactQuery/useAppQuery';
import StaffHTTPService from 'services/HTTPService/staff/StaffHTTPService';

import { parseStringIntoDayjsDate } from 'utils/parsers/dateTime/parseStringIntoDayjsDate';
import { getMonthFromDayjsMonth } from 'utils/getMonthFromDayjsMonth';
import { GroupId, SettlementChildrenSortBy, SorterParams } from 'types';
import { useSorter } from 'hooks/useSorter/useSorter';

type ChildrenSettlementPageNetwork = {
    filtersValues: {
        groupsFilterValue: GroupId[] | null;
        searchFilterDebounceValue: string;
        dateFilterValue: string | null;
    };
    currentPage: string;
    currentPageSize: string;
};

export const useChildrenSettlementPageNetworkManager = ({
    currentPage,
    currentPageSize,
    filtersValues,
}: ChildrenSettlementPageNetwork) => {
    const { sortData, handleSorterChange } = useSorter();

    const { groupsFilterValue, searchFilterDebounceValue, dateFilterValue } = filtersValues;

    const selectedDate = dateFilterValue ? parseStringIntoDayjsDate(dateFilterValue) : dayjs();
    const selectedYear = selectedDate.year();
    const selectedMonth = getMonthFromDayjsMonth(selectedDate.month());

    const {
        data: settlementsData,
        isInitialLoading: isSettlementsDataInitialFetching,
        isRefetching: isSettlementsDataRefetching,
        isError: isSettlementsDataError,
    } = useAppQuery(
        'SETTLEMENTS',
        [
            searchFilterDebounceValue,
            currentPage,
            currentPageSize,
            dateFilterValue,
            groupsFilterValue,
            sortData.field,
            sortData.order,
        ],
        () =>
            StaffHTTPService.settlements.getMonthlySettlementsV2({
                sorterParams: sortData as SorterParams<SettlementChildrenSortBy>,
                groupIds: groupsFilterValue,
                search: searchFilterDebounceValue,
                yearMonth: `${selectedYear}-${selectedMonth.toString().padStart(2, '0')}`,
                paginationParams: { size: currentPageSize, page: currentPage },
            }),
        { refetchOnWindowFocus: false },
    );

    const currentDate = dayjs();
    const currentYear = currentDate.year();
    const isBeforeNewSchoolYear = isBeforeSchoolYear(dayjs(), currentYear);

    const [fromYearGroupsFilterValue, toYearGroupsFilterValue] = isBeforeNewSchoolYear
        ? [currentYear - 1, currentYear]
        : [currentYear, currentYear + 1];

    const {
        data: groupsData,
        isFetching: isGroupsDataFetching,
        isError: isGroupsDataError,
    } = useAppQuery(
        'GROUPS',
        [fromYearGroupsFilterValue, toYearGroupsFilterValue],
        () =>
            StaffHTTPService.groups.getGroups({
                fromYear: fromYearGroupsFilterValue,
                toYear: toYearGroupsFilterValue,
            }),
        {},
    );

    return {
        sortData,
        handleSorterChange,
        selectedTime: {
            selectedDate,
            selectedYear,
            selectedMonth,
        },
        settlementsData,
        isSettlementsDataInitialFetching,
        isSettlementsDataRefetching,
        isDataError: isSettlementsDataError || isGroupsDataError,
        groupsData,
        isGroupsDataFetching,
    };
};
